/**
 * XPEN-3328: workflow "category is" stores an XPENS hex id; leftover names
 * (unmigrated) are listed as that name. "client category is" stores a root
 * imputation id. A condition is stale when that identity is no longer live.
 */

import { isXpensCategoryHexId } from "./xpens-categories";
import { imputationDocumentId, mongoExtendedJsonId } from "../pages/review-expense/utils/contract-expense-json";

const normalizeCategoryName = (str) => (str ? String(str).toLowerCase().replace(/[\s.]/g, "") : "");

function leftoverNameMatchesLiveRoot(value, rootImputations) {
  const name = typeof value === "string" ? value : mongoExtendedJsonId(value);
  const target = normalizeCategoryName(name);
  if (!target) return false;
  return rootImputations.some((row) => normalizeCategoryName(row?.category) === target);
}

function staleLabel(value) {
  if (typeof value === "string" && value) return value;
  return mongoExtendedJsonId(value);
}

function collectConditions(branches, out) {
  if (!Array.isArray(branches)) return;
  branches.forEach((branch) => {
    if (!Array.isArray(branch?.steps)) return;
    branch.steps.forEach((step) => {
      if (Array.isArray(step?.branches) && step.branches.length > 0) {
        collectConditions(step.branches, out);
        return;
      }
      if (Array.isArray(step?.conditions)) {
        out.push(...step.conditions);
      }
    });
  });
}

/**
 * @param {object} workflow
 * @param {{ xpensCategories?: Array, rootImputations?: Array }} identities
 * @returns {string[]} distinct stale labels to show in the admin warning
 */
export function findStaleCategoryConditions(workflow, identities = {}) {
  const xpensCategories = identities.xpensCategories;
  const rootImputations = identities.rootImputations;
  const categoriesReady = Array.isArray(xpensCategories) && xpensCategories.length > 0;
  const rootsReady = Array.isArray(rootImputations) && rootImputations.length > 0;

  const xpensIds = new Set(
    (xpensCategories || [])
      .map((c) => (mongoExtendedJsonId(c._id) || "").toLowerCase())
      .filter(Boolean)
  );
  const rootIds = new Set(
    (rootImputations || [])
      .map((row) => imputationDocumentId(row).toLowerCase())
      .filter(Boolean)
  );

  const conditions = [];
  collectConditions(workflow?.branches, conditions);

  const stale = new Set();
  conditions.forEach((condition) => {
    const value = condition?.value;
    if (!value) return;

    if (condition.type === "category") {
      if (isXpensCategoryHexId(value)) {
        if (!categoriesReady) return;
        const id = mongoExtendedJsonId(value).toLowerCase();
        if (!xpensIds.has(id)) stale.add(mongoExtendedJsonId(value));
        return;
      }
      if (rootsReady && leftoverNameMatchesLiveRoot(value, rootImputations)) return;
      const leftover = staleLabel(value);
      if (leftover) stale.add(leftover);
      return;
    }

    if (condition.type === "client_category") {
      if (!rootsReady) return;
      const id = (mongoExtendedJsonId(value) || "").toLowerCase();
      if (!id || !rootIds.has(id)) {
        const label = staleLabel(value);
        if (label) stale.add(label);
      }
    }
  });
  return Array.from(stale);
}

export function workflowHasStaleCategoryCondition(workflow, identities) {
  return findStaleCategoryConditions(workflow, identities).length > 0;
}
